import { useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Types
import { AnimationPreset } from '@/types';

// Utils
import { combineClasses } from '@/utils';

export interface AnimatedSuffixProps {
  /** The text content to animate */
  text: string;
  /** Optional CSS class names */
  className?: string;
  /** Frame number when animation should start */
  startFrame: number;
}

/**
 * AnimatedSuffix - Smooth Slide-in Text Animation
 *
 * Animates text with a smooth slide-in effect from the left.
 * Perfect for suffix text that appears after a main element.
 * Features include:
 * - Horizontal slide from left
 * - Scale with subtle overshoot
 * - Smooth opacity transition
 */
export const AnimatedSuffix = ({
  text,
  className = '',
  startFrame,
}: AnimatedSuffixProps): React.ReactNode => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const progress = spring({
    frame: frame - startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.TEXT_SLIDE],
  });

  const opacity = interpolate(progress, [0, 0.4], [0, 1], CLAMP_BOTH);
  const translateX = interpolate(progress, [0, 1], [-30, 0], CLAMP_BOTH);
  const scale = interpolate(progress, [0, 0.6, 1], [0.8, 1.2, 1], CLAMP_BOTH);

  return (
    <span
      className={combineClasses(className, 'inline-block')}
      style={{
        opacity,
        transform: `translateX(${translateX}px) scale(${scale})`,
      }}
    >
      {text}
    </span>
  );
};
